/**
 * "Özet" sekmesi — Power BI raporundaki ilk sayfanın karşılığı.
 *
 * Üstte tek satırlık künye şeridi (plan zamanı + beş ölçüt + iki pasta),
 * altında dört grafik: beyaz/silver gövde ön stoğu ve beyaz/silver gövde
 * üretim + stok seviyeleri.
 *
 * Ölçütlerin hangi alandan geldiği `api.js`'te açıklanıyor; buradaki config
 * yalnız görünümü tarif eder.
 */
import {
  AMBER,
  BLUE,
  CORAL,
  DASHBOARD_THEME,
  ORANGE,
  SILVER,
  WHITE,
} from "../theme";
import { PROD_PATH, STARTSTOP_PATH, STOK_PATH } from "../api";
import { TEXT_KPI } from "../charts/TextKpi";

/** Grid'in toplam satır sayısı — en alttaki kart burada biter. */
export const OZET_ROWS = 24;

/**
 * Kırılımlar. Masaüstünde (kırılım yok) satır yüksekliği ölçülen `rowPx`,
 * pano tek ekrana oturur. Tablet/telefonda satır sabit, dikey kaydırma
 * serbest: on kartı bir telefon ekranına okunaklı sığdırmak mümkün değil.
 */
const BREAKPOINTS = [
  { name: "md", maxWidth: 1024, columns: 12, rowHeight: 46 },
  { name: "sm", maxWidth: 640, columns: 4, rowHeight: 42 },
];

/** Tablet (12 sütun) yerleşimi — id → { x, y, w, h }. */
const MD = {
  "plan-tarihi": { x: 0, y: 0, w: 4, h: 3 },
  "kpi-govde-on": { x: 4, y: 0, w: 4, h: 3 },
  "kpi-govde": { x: 8, y: 0, w: 4, h: 3 },
  "kpi-durus": { x: 0, y: 3, w: 3, h: 3 },
  "kpi-ilk-renk": { x: 3, y: 3, w: 9, h: 3 },
  "pie-us": { x: 0, y: 6, w: 6, h: 7 },
  "pie-renk": { x: 6, y: 6, w: 6, h: 7 },
  "beyaz-on-stok": { x: 0, y: 13, w: 12, h: 9 },
  "silver-on-stok": { x: 0, y: 22, w: 12, h: 9 },
  "beyaz-uretim": { x: 0, y: 31, w: 12, h: 9 },
  "silver-uretim": { x: 0, y: 40, w: 12, h: 9 },
};

/** Telefon (4 sütun) yerleşimi — her şey alt alta. */
const SM = {
  "plan-tarihi": { x: 0, y: 0, w: 4, h: 3 },
  "kpi-govde-on": { x: 0, y: 3, w: 2, h: 3 },
  "kpi-govde": { x: 2, y: 3, w: 2, h: 3 },
  "kpi-durus": { x: 0, y: 6, w: 2, h: 3 },
  "kpi-ilk-renk": { x: 2, y: 6, w: 2, h: 3 },
  "pie-us": { x: 0, y: 9, w: 4, h: 7 },
  "pie-renk": { x: 0, y: 16, w: 4, h: 7 },
  "beyaz-on-stok": { x: 0, y: 23, w: 4, h: 9 },
  "silver-on-stok": { x: 0, y: 32, w: 4, h: 9 },
  "beyaz-uretim": { x: 0, y: 41, w: 4, h: 9 },
  "silver-uretim": { x: 0, y: 50, w: 4, h: 9 },
};

/** Bir kartın kırılım yerleşimleri. */
const L = (id) => ({ md: MD[id], sm: SM[id] });

/**
 * Kartların küçük katları — rapordaki görsel süzgeçleriyle birebir.
 * Beyaz kartı tek renkle sınırlı; Silver kartının süzgeci "Silver veya Cast
 * Iron" olduğu için iki kat çiziyor. Cast Iron katı, API o ürünleri Beyaz
 * renkle döndürdüğü sürece boş kalır — raporda da öyle görünüyor.
 */
const BEYAZ_KATLAR = [{ ek: "beyaz", ad: "Beyaz" }];
const SILVER_KATLAR = [
  { ek: "silver", ad: "Silver" },
  { ek: "castiron", ad: "Cast Iron" },
];

/**
 * @param rowPx ölçülen satır yüksekliği px
 * @param gap grid boşluğu px
 * @param refreshKey periyodik yenileme anahtarı (url'e girer, önbelleği tazeler)
 * @param tvMode TV modunda yazılar/aralıklar büyür
 */
export function buildOzetConfig(rowPx, gap, refreshKey = 0, tvMode = false) {
  /** Aynı ucun detay ve özet kaynakları; fragment yalnız dönüşümü ayırır. */
  const PROD = { url: `${PROD_PATH}?_t=${refreshKey}` };
  const PROD_OZET = { url: `${PROD_PATH}?_t=${refreshKey}#ozet` };
  const SS_OZET = { url: `${STARTSTOP_PATH}?_t=${refreshKey}#ozet` };
  const STOK_OZET = { url: `${STOK_PATH}?_t=${refreshKey}#ozet` };

  // TV modunda ilk, laptop/masaüstünde ikinci değer kullanılır.
  const V = (tv, base) => (tvMode ? tv : base);

  const kpiTile = (id, title, source, field, layout, options = {}) => ({
    id,
    source,
    layout,
    layouts: L(id),
    chart: {
      type: "kpi",
      title,
      dimensions: [],
      measures: [{ key: field, label: "" }],
      options: {
        showLabel: false,
        aggregate: "last",
        className: "pano-kpi",
        titleStyle: { size: V(18, 11) },
        valueStyle: { size: V(42, 28) },
        ...options,
      },
    },
  });

  /**
   * İki pasta aynı ölçülerde çizilsin diye ortak ayarlar.
   *
   * Yarıçap SABİT PİKSEL DEĞİL, oran. Künye şeridi alçak bir kart (dört
   * satır) ve satır yüksekliği ekrana göre ölçülüyor; sabit yarıçap küçük
   * ekranda daireyi karttan taşırıyordu. Oran verilince daire kalan alana
   * göre çizilir, her boyutta sığar.
   */
  const pieOptions = (palette) => ({
    height: "100%",
    dataLabels: true,
    format: "percent",
    decimals: 2,
    legendPosition: "bottom",
    legendHeight: V(26, 18),
    palette,
    outerRadius: "92%",
    margin: { left: 4, right: 4, top: 0, bottom: 0 },
    legendStyle: { size: V(13, 9) },
    labelStyle: { size: V(14, 10), weight: 600 },
  });

  /** Dört grafiğin ortak ekseni: 20 dakikalık periyotlar. */
  const zamanEkseni = {
    rollup: "sum",
    height: "100%",
    grid: false,
    legendPosition: "top",
    legendHeight: V(26, 20),
    xTickAngle: -90,
    xAxisHeight: V(70, 52),
    // Eksen sütunu dar tutulur; kazanılan genişlik çizim alanına gider.
    yAxisWidth: V(44, 32),
    axisStyle: { size: V(14, 9) },
    labelStyle: { size: V(15, 10) },
    legendStyle: { size: V(16, 11) },
    titleStyle: { size: V(22, 15) },
    responsive: {
      md: { axisStyle: { size: 9 }, dataLabels: false },
      sm: { axisStyle: { size: 8 }, dataLabels: false, legendStyle: { size: 9 } },
    },
  };

  /**
   * Küçük katlı kart. Rapordaki görseller `color` alanına göre küçük katlara
   * bölünüyor: Silver kartının süzgeci "Silver veya Cast Iron" olduğu için iki
   * kat çiziliyor, Beyaz kartı tek kat.
   *
   * Kütüphanede küçük kat diye bir özellik yok; aynı düzen grup kartıyla
   * kuruluyor — alt alta dizilmiş, aynı kaynağı paylaşan grafikler. Raporda
   * olduğu gibi legend yalnız üst katta, saat etiketleri yalnız alt katta
   * yazılır; aradaki katlar dikey yeri grafiğe bırakır.
   *
   * @param katlar [{ ek, ad }] — sütun adı eki ve katın başlığı
   * @param yap (ek, { ilk, son }) => ChartConfig
   */
  const katliKart = (id, baslik, katlar, layout, yap) => {
    if (katlar.length === 1) {
      return {
        id,
        source: PROD,
        layouts: L(id),
        layout,
        chart: { ...yap(katlar[0].ek, { ilk: true, son: true }), title: baslik },
      };
    }
    return {
      id,
      title: baslik,
      direction: "column",
      source: PROD,
      layouts: L(id),
      layout,
      charts: katlar.map((kat, i) => {
        const c = yap(kat.ek, { ilk: i === 0, son: i === katlar.length - 1 });
        return {
          flex: 1,
          chart: {
            ...c,
            // Kat başlığı kartın başlığı değil; küçük ve sola yaslı yazılır
            // (hizalama CSS'te, .pano-kat sınıfı üzerinden).
            title: kat.ad,
            options: {
              ...c.options,
              className: `${c.options.className} pano-kat`,
              titleStyle: { size: V(15, 11), color: DASHBOARD_THEME.muted },
            },
          },
        };
      }),
    };
  };

  /**
   * Kat başına eksen/legend ayarı — raporun küçük kat düzeniyle aynı.
   * Legend yalnız üst katta, saat etiketleri yalnız alt katta yer kaplar;
   * aradaki katlar bu yeri çizim alanına verir.
   */
  const katEkseni = ({ ilk, son }) => ({
    legend: ilk,
    // Legend ile serilerin değer etiketleri aynı şeride düşüp üst üste
    // biniyordu; şerit yükselince etiketler altında kalıyor.
    legendHeight: ilk ? V(34, 26) : 0,
    xAxis: son,
    xAxisHeight: son ? V(70, 52) : 0,
  });

  /** "Gövde Ön Stok" grafiği — F14 / F23 stok eğrileri. */
  const onStokChart = (renkEki, kat) => ({
    type: "line",
    dimensions: ["zaman"],
    measures: [
      { key: `${renkEki}_on_f14`, label: "F14", color: ORANGE },
      { key: `${renkEki}_on_f23`, label: "F23", color: BLUE },
    ],
    options: {
      ...zamanEkseni,
      ...katEkseni(kat),
      className: "pano-on-stok",
      dataLabels: true,
      margin: { top: V(30, 20) },
    },
  });

  /** "Gövde Üretim ve Stok Seviyeleri" grafiği — çubuk üretim + çizgi stok. */
  const uretimStokChart = (renkEki, kat) => ({
    // Rapordaki görsel "çizgi ve yığılmış sütun grafiği": iki üretim ölçüsü
    // sütun olarak yığılır, iki stok ölçüsü sağ eksende çizgi olur.
    type: "combo-stacked",
    dimensions: ["zaman"],
      measures: [
        {
          key: `${renkEki}_u_uretim`,
          label: "U Gövde Üretim",
          type: "bar",
          color: AMBER,
          // Raporda sütunların üstünde değer yazmıyor; yığılı barda etiket
          // dilimin içine düşeceği için burada da kapalı.
          dataLabels: false,
        },
        {
          key: `${renkEki}_single_uretim`,
          label: "Single Gövde Üretim",
          type: "bar",
          color: CORAL,
          dataLabels: false,
        },
        {
          key: `${renkEki}_u_stok`,
          label: "U Gövde Stok",
          type: "line",
          color: WHITE,
          axis: "right",
        },
        {
          key: `${renkEki}_single_stok`,
          label: "Single Gövde Stok",
          type: "line",
          color: BLUE,
          axis: "right",
        },
      ],
    options: {
      ...zamanEkseni,
      ...katEkseni(kat),
      className: "pano-uretim-stok",
      dataLabels: true,
      barSize: 14,
      y2AxisWidth: V(44, 32),
      margin: { top: V(34, 22) },
      // Rapordaki "Araç ipuçları: Sipariş No". Periyot başına tek sipariş
      // döndüğü için toplama sonrası da doğru satırı gösterir.
      tooltipFields: [{ key: "order_nr", label: "Sipariş No" }],
      responsive: {
        md: { barSize: 10, axisStyle: { size: 9 }, dataLabels: false },
        sm: { barSize: 6, axisStyle: { size: 8 }, dataLabels: false },
      },
    },
  });

  return {
    layout: { columns: 24, rowHeight: rowPx, gap, breakpoints: BREAKPOINTS },

    tiles: [
      // --- Künye: planın üretildiği an ---
      // Üstündeki SmartOpt logo kartı kaldırıldı; kart boşalan yeri alarak
      // şeridin tam yüksekliğine uzadı.
      {
        id: "plan-tarihi",
        source: SS_OZET,
        layouts: L("plan-tarihi"),
        layout: { x: 0, y: 0, w: 2, h: 4 },
        chart: {
          // Değer metin; kütüphanenin `kpi` kartı sayıya indirger (bkz.
          // charts/TextKpi.jsx).
          type: TEXT_KPI,
          dimensions: [],
          measures: [{ key: "plan_tarihi", label: "" }],
          options: {
            // Başlık `title` değil `kpiTitle` — gerekçesi TextKpi.jsx'te.
            kpiTitle: "Plan Güncellenme Tarihi",
            className: "pano-kpi pano-plan-tarihi",
            titleStyle: { size: V(13, 10) },
            valueStyle: { size: V(17, 12) },
          },
        },
      },

      // --- İlk iki ölçüt: başlıkları "üretim" ama ölçtükleri stok ---
      // (getDailyStockManagement / stock_amount; ayrıntısı api.js'te)
      // Raporda binlik ayracı yok — "6350", "6.350" değil.
      kpiTile(
        "kpi-govde-on",
        "Gövde Ön Üretim",
        STOK_OZET,
        "stok_govde_on",
        { x: 2, y: 0, w: 3, h: 4 },
        { format: "text" }
      ),
      kpiTile(
        "kpi-govde",
        "Gövde Üretim",
        STOK_OZET,
        "stok_govde",
        { x: 5, y: 0, w: 3, h: 4 },
        { format: "text" }
      ),
      {
        id: "kpi-durus",
        source: SS_OZET,
        layouts: L("kpi-durus"),
        layout: { x: 8, y: 0, w: 2, h: 4 },
        chart: {
          type: "kpi",
          title: "Duruş Sayısı",
          dimensions: [],
          measures: [{ key: "durus_sayisi", label: "" }],
          options: {
            showLabel: false,
            aggregate: "last",
            className: "pano-kpi",
            titleStyle: { size: V(18, 11) },
            valueStyle: { size: V(42, 28) },
          },
        },
      },
      {
        id: "kpi-ilk-renk",
        source: SS_OZET,
        layouts: L("kpi-ilk-renk"),
        layout: { x: 10, y: 0, w: 4, h: 4 },
        chart: {
          // Değişim yoksa "Renk Değişimi Yok" yazılıyor — metin KPI.
          type: TEXT_KPI,
          dimensions: [],
          measures: [{ key: "ilk_renk_degisim", label: "" }],
          options: {
            kpiTitle: "İlk Renk Değişim",
            className: "pano-kpi",
            titleStyle: { size: V(18, 11) },
            // Değişim olduğunda tam zaman damgası yazılıyor ("2026-09-11
            // 12:40:00"); punto ona göre, "Renk Değişimi Yok" da sığıyor.
            valueStyle: { size: V(24, 15) },
          },
        },
      },

      // --- İki pasta ---
      {
        id: "pie-us",
        source: PROD_OZET,
        layouts: L("pie-us"),
        layout: { x: 14, y: 0, w: 5, h: 4 },
        chart: {
          type: "pie",
          title: "U / Single ölçütüne göre Üretim",
          // Geniş veri: tek satır, her ölçü bir dilim.
          dimensions: [],
          measures: [
            { key: "u_pay", label: "U" },
            { key: "single_pay", label: "Single" },
          ],
          options: {
            ...pieOptions([AMBER, BLUE]),
            className: "pano-pie",
            titleStyle: { size: V(13, 10) },
          },
        },
      },
      {
        id: "pie-renk",
        // U/Single pastasından FARKLI uç: rapor renk kırılımını
        // getBSHStartStopResults satırlarını sayarak kuruyor.
        source: SS_OZET,
        layouts: L("pie-renk"),
        layout: { x: 19, y: 0, w: 5, h: 4 },
        chart: {
          type: "pie",
          title: "Renk ölçütüne göre Üretim",
          dimensions: [],
          measures: [
            { key: "beyaz_pay", label: "Beyaz" },
            { key: "silver_pay", label: "Silver" },
          ],
          options: {
            ...pieOptions([WHITE, SILVER]),
            className: "pano-pie",
            titleStyle: { size: V(13, 10) },
            // Beyaz dilimin üstünde beyaz yazı okunmaz.
            labelStyle: { size: V(15, 11), weight: 600, color: "#1a1a1a" },
          },
        },
      },

      // --- Gövde ön stokları ---
      katliKart(
        "beyaz-on-stok",
        "Beyaz Gövde Ön Stok",
        BEYAZ_KATLAR,
        { x: 0, y: 4, w: 12, h: 10 },
        onStokChart
      ),
      katliKart(
        "silver-on-stok",
        "Silver Gövde Ön Stok",
        SILVER_KATLAR,
        { x: 12, y: 4, w: 12, h: 10 },
        onStokChart
      ),

      // --- Gövde üretim ve stok seviyeleri ---
      katliKart(
        "beyaz-uretim",
        "Beyaz Gövde Üretim ve Stok Seviyeleri (Saatlik)",
        BEYAZ_KATLAR,
        { x: 0, y: 14, w: 12, h: 10 },
        uretimStokChart
      ),
      katliKart(
        "silver-uretim",
        "Silver Gövde Üretim ve Stok Seviyeleri (Saatlik)",
        SILVER_KATLAR,
        { x: 12, y: 14, w: 12, h: 10 },
        uretimStokChart
      ),
    ],
  };
}

export { DASHBOARD_THEME };
